import * as React from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import CondoUnitInfoCard from "../../condo-units/components/CondoUnitInfoCard";
import { useToast } from "../../../components/shared/ToastProvider";

import "../../../styles/main.css";
import "../../../styles/pages/condoUnitDetail.css";

// UI-only form for creating/updating a condo payment from the Condo Payment page.
export default function CondoPaymentFormModal({
  open,
  onClose,
  mode = "edit", // "create" for Add Payment
  condoItems = [],
  initialValues = {}
}) {
  const isEdit = mode === "edit";
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    showToast({
      message: isEdit ? "Condo payment updated" : "Condo payment created",
      severity: "success"
    });
    if (onClose) onClose();
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {isEdit ? "Update" : "Create"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Condo Payment" : "Add Condo Payment"}
      actions={actions}
      maxWidth="lg"
      fullWidth
    >
      {/* Condo Unit Info */}
      <Box mb={2} className="modal-condo-unit-info-card">
        <CondoUnitInfoCard
          title="Condo Unit Info"
          items={condoItems}
          showEdit={false}
        />
      </Box>

      {/* Form fields - UI only */}
      <Box className="modal-form-grid modal-form-grid--spaced">
        {/* Two-column layout: left and right */}
        <Box className="modal-form-row modal-form-row--two-cols">
          {/* Left column */}
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Acct Entry Date"
              required
              InputLabelProps={{ shrink: true }}
              defaultValue={initialValues.acctEntryDateRaw || ""}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Amount Due"
              required
              defaultValue={initialValues.amountDue || ""}
            />
            <Box display="flex" gap={1} alignItems="center" width="100%">
              <FormControl size="small" fullWidth>
                <InputLabel>Use Advance Balance</InputLabel>
                <Select
                  defaultValue={initialValues.useAdvanceBalance || "No"}
                  label="Use Advance Balance"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                disabled
                defaultValue={initialValues.advanceBalance || "PHP 0.00"}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Receipt No"
              defaultValue={initialValues.receiptNo || ""}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>RF No</InputLabel>
              <Select
                defaultValue={initialValues.rfNo || "11228"}
                label="RF No"
              >
                <MenuItem value="11228">11228</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Condo Company</InputLabel>
              <Select
                defaultValue={initialValues.condoCompany || "Caspo"}
                label="Condo Company"
              >
                <MenuItem value="Caspo">Caspo</MenuItem>
                <MenuItem value="Oriental KH Management">Oriental KH Management</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Right column */}
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            <TextField
              fullWidth
              size="small"
              label="Check No"
              defaultValue={initialValues.checkNo || ""}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Payment Date"
              required
              InputLabelProps={{ shrink: true }}
              defaultValue={initialValues.paymentDateRaw || ""}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Condo Sharing</InputLabel>
              <Select
                defaultValue={initialValues.condoSharing || "Not Shared"}
                label="Condo Sharing"
              >
                <MenuItem value="Not Shared">Not Shared</MenuItem>
                <MenuItem value="Shared">Shared</MenuItem>
              </Select>
            </FormControl>
            <Box
              display="flex"
              alignItems="baseline"
              gap={1}
              mt={0.5}
              mb={0.5}
            >
              <span className="modal-inline-label">Month(s):</span>
              <span className="modal-inline-value">
                {initialValues.months || 6} Month(s)
              </span>
            </Box>
          </Box>
        </Box>

        {/* Remarks spanning full width */}
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          className="modal-form-row"
          label="Remarks"
          defaultValue={initialValues.remarks || ""}
        />
      </Box>
    </Modal>
  );
}
